import React, { useRef, useState } from 'react';
import { usePhotoUrl } from '../../lib/gallery/usePhotoUrl';
import { usePrefersReducedMotion } from '../../lib/gallery/usePrefersReducedMotion';
import { bump } from '../../lib/gallery/metrics';
import type { PhotoItem } from '../../lib/photo-sources/types';

interface PhotoTileProps {
  item: PhotoItem;
  /** Rendered CSS width. Drives the resolution request. */
  columnWidth: number;
  /**
   * Explicit rendered height. Supplied by the editorial layout, which positions tiles absolutely
   * and already knows every box. When present the tile fills its parent instead of deriving a
   * height from the aspect ratio, and skips `content-visibility` — windowing has already removed
   * the off-screen tiles, so deferring render work would only add cost.
   */
  height?: number;
  onOpen?: () => void;
}

/** Cap at 2 — beyond that the bytes cost more than the sharpness is worth. */
function pixelRatio(): number {
  return Math.min(typeof window === 'undefined' ? 1 : window.devicePixelRatio || 1, 2);
}

const EASE = 'cubic-bezier(0.22, 1, 0.36, 1)';

/**
 * Deliberately free of framer-motion. One motion component per tile means 449 subscriptions to
 * the animation frame loop; the reveal is a two-property CSS transition and the compositor can
 * run it without touching JS at all.
 */
const PhotoTile: React.FC<PhotoTileProps> = ({ item, columnWidth, height, onOpen }) => {
  const aspect = item.width / item.height;
  const dpr = pixelRatio();
  const windowed = height !== undefined;
  const { ref, state } = usePhotoUrl(item, {
    w: Math.round(columnWidth * dpr),
    h: Math.round((height ?? columnWidth / aspect) * dpr),
  });
  // Seeded from cache means this tile is remounting after windowing scrolled it out and back.
  // It was already on screen a moment ago, so replaying the reveal reads as a flash.
  const seededFromCache = useRef(state.status === 'ready');
  const [decoded, setDecoded] = useState(seededFromCache.current);
  const reduceMotion = usePrefersReducedMotion();
  bump('tileRenders');
  const instant = seededFromCache.current || reduceMotion;

  // Most boxes now match their photo's aspect exactly and crop nothing. Where a box is wider
  // than its photo, the crop takes it out of the top and bottom — and heads sit above centre,
  // so a centred crop is what cuts faces off. Bias upward only in that case; a vertical crop
  // stays centred, where subjects are usually well placed horizontally.
  const boxAspect = height !== undefined ? columnWidth / height : aspect;
  const cropsVertically = boxAspect > aspect * 1.02;
  const objectPosition = cropsVertically ? '50% 32%' : '50% 50%';

  // The reveal rides on decode rather than its own observer. `usePhotoUrl` already knows when a
  // tile is near the viewport, so a separate visibility signal would be redundant work.
  const revealed = state.status === 'ready' && decoded;

  return (
    <div
      ref={ref as React.RefObject<HTMLDivElement>}
      className="gallery-tile group relative w-full overflow-hidden rounded-lg ring-1 ring-white/10"
      style={{
        // The LQIP: the tile is never empty, so there is nothing to "pop in" against.
        backgroundColor: item.placeholder,
        ...(windowed
          ? { height: '100%' }
          : {
              aspectRatio: `${item.width} / ${item.height}`,
              // Skip rendering work for tiles far off-screen. The intrinsic size keeps the
              // scrollbar honest while they're skipped.
              //
              // Order is `width height`. This was transposed until 2026-07-26, which meant every
              // skipped tile claimed the wrong height — so each one that scrolled into view
              // resized, relaying out its column and the document beneath it. At fling speed
              // that was dozens of full-tree layouts per frame, and it is why profiles showed
              // 300ms frames with zero JavaScript running.
              contentVisibility: 'auto' as const,
              containIntrinsicSize: `${Math.round(columnWidth)}px ${Math.round(columnWidth / aspect)}px`,
            }),
      }}
    >
      {state.status === 'ready' && (
        <img
          src={state.url}
          alt=""
          decoding="async"
          onLoad={() => setDecoded(true)}
          className="absolute inset-0 h-full w-full object-cover"
          style={{
            objectPosition,
            opacity: revealed ? 1 : 0,
            // No `will-change`: at this count it would promote hundreds of compositor layers.
            // Transform and opacity get promoted for the duration of the transition anyway.
            transform: revealed || instant ? 'none' : 'translate3d(0, 18px, 0) scale(1.03)',
            transition: instant ? 'none' : `opacity 550ms ${EASE}, transform 550ms ${EASE}`,
          }}
        />
      )}

      {state.status === 'error' && (
        <div className="absolute inset-0 grid place-items-center bg-black/40 p-2 text-center text-[10px] text-white/70">
          Couldn&rsquo;t load
        </div>
      )}

      {onOpen && (
        <button
          type="button"
          onClick={onOpen}
          aria-label="View photo full screen"
          className="absolute inset-0 cursor-zoom-in focus-visible:ring-2 focus-visible:ring-brand-400"
        >
          <span className="absolute inset-0 bg-black/0 transition-colors duration-300 group-hover:bg-black/15" />
        </button>
      )}
    </div>
  );
};

export default PhotoTile;
